import React, { useEffect, useRef, useState } from 'react';
import {
  breakpoints,
  Button,
  Column,
  Columns,
  Heading,
  Inline,
  PageBlock,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { Collection, DevBar } from '@eti/debug';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { useGA4PageViewEvent } from '../../common/data-layer/ecommerce/custom-hooks/useGA4PageViewEvent';
import { routes } from '../../constants/routesAndApisConstants';
import { RESPONSIVE_PAGEVIEW_DETAILS } from '../../constants/dataLayerEventsConstants';
import Cookies from 'js-cookie';
import { ClockCountdown } from '@phosphor-icons/react';
import { PaymentStatus } from '@eti/schema-types';
import PaymentMethodLogo from '../payment/components/PaymentMethodLogo';
import { isDev } from '../../common/debug/utils';
import { DebugCommonComponents } from '../../common/debug/components';
import { paymentMethodCodes } from '../payment/constants/paymentMethods';
import ProcessingQrCode from '../processing-common/ProcessingQrCode';
import CountdownTimer from '../processing-common/CountdownTimer';
import { removeCookies } from '../payment/utils/cookies';
import usePaymentStatus from '../../common/custom-hooks/usePaymentStatus';
import { useCancelPayment } from '../../common/custom-hooks/useCancelPayment';
import Loading from '../../common/loading/components/Loading';
import ErrorPage from '../status-pages/components/ErrorPage';
import PaymentTimeoutModal from '../../common/error-modals/PaymentTimeoutModal';
import { PAYNOW_START_TIME, payNowCookies } from '../payment/payment-methods/paynow/constants';

const columnStyles = css`
  margin: 0 auto;
`;

const headingWrapperStyles = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const cancelButtonStyles = css`
  padding-inline: 30px;
`;

const pageStyles = css`
  background-color: ${vars.colors.page.background.secondary};
  height: 100%;
`;

const removeCookieData = () => {
  removeCookies([...payNowCookies]);
};

const ProcessingPayNowPage = () => {
  const { t } = useTranslation();
  useGA4PageViewEvent({
    responsive_url: routes.PROCESSING_PAYNOW,
    page_type: RESPONSIVE_PAGEVIEW_DETAILS.PROCESSING_PAYNOW.page_type,
    page_type_detailed: RESPONSIVE_PAGEVIEW_DETAILS.PROCESSING_PAYNOW.page_type_details,
  });
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { qrCodeData, redirectUrl, paymentTransactionId, paymentPageUrl } = Cookies.get();
  const hasRedirectedRef = useRef(false);
  const { error, loading, status, stopPolling } = usePaymentStatus(paymentTransactionId);

  const resetStartTime = () => {
    sessionStorage.removeItem(PAYNOW_START_TIME);
  };

  const cancelPayment = useCancelPayment(
    resetStartTime,
    removeCookieData,
    setIsRedirecting,
    paymentPageUrl,
  );

  useEffect(() => {
    if (status === PaymentStatus.Error) {
      cancelPayment('GENERIC_ERROR');
    }
  }, [cancelPayment, status]);

  useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }

    if (status === PaymentStatus.Completed && redirectUrl) {
      hasRedirectedRef.current = true;

      resetStartTime();
      removeCookies([...payNowCookies]);

      window.location.assign(redirectUrl);
    }
  }, [redirectUrl, status]);

  useEffect(() => {
    if (isExpired) {
      stopPolling();
    }
  }, [isExpired, stopPolling]);

  if (loading || isRedirecting) {
    return <Loading />;
  }

  if ((!redirectUrl || error) && !hasRedirectedRef.current) {
    return <ErrorPage />;
  }

  if (isExpired) {
    return <PaymentTimeoutModal onClick={cancelPayment} />;
  }

  return (
    <PageBlock className={pageStyles} withPaddingY>
      <Columns>
        <Column
          className={columnStyles}
          width={{
            [breakpoints._0]: '6/6',
            [breakpoints._768]: '5/6',
            [breakpoints._970]: '4/6',
          }}
        >
          <Stack spacing={16}>
            <div className={headingWrapperStyles}>
              <Heading level={1}>{t('Processing.PayNow.Title')}</Heading>
              <PaymentMethodLogo id="paynow" />
            </div>
            <Stack spacing={24}>
              <Stack align="center" spacing={24}>
                {qrCodeData && (
                  <ProcessingQrCode code={paymentMethodCodes.PAYNOW} data={qrCodeData} />
                )}
                <Inline alignY="center" spacing={4}>
                  <ClockCountdown aria-hidden="true" size={20} />
                  <CountdownTimer
                    code={paymentMethodCodes.PAYNOW}
                    onExpire={() => setIsExpired(true)}
                    storageKey={PAYNOW_START_TIME}
                  />
                </Inline>
              </Stack>
              <Text asHtml variant={textVariants._16}>
                {t('Processing.PayNow.Description')}
              </Text>
              <Inline align="end">
                <Button className={cancelButtonStyles} onClick={() => cancelPayment()}>
                  {t('Processing.PayNow.Cancel.Button')}
                </Button>
              </Inline>
            </Stack>
          </Stack>
        </Column>
      </Columns>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </PageBlock>
  );
};
export default ProcessingPayNowPage;
